import { IconButton } from "@circleco/compass/components/IconButton";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import { SearchInputField } from "./SearchInputField";

const DEBOUNCE_MS = 300;
const MOBILE_INPUT_CLASS_NAME = "md:hidden";

interface CollapsibleSearchInputProps {
  inputClassName?: string;
  isExpandedOnMobile?: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const CollapsibleSearchInput = ({
  inputClassName,
  isExpandedOnMobile = false,
  onChange,
  placeholder = t("spaces.form.edit.members.search_members"),
}: CollapsibleSearchInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedOnChange = useRef(debounce(onChange, DEBOUNCE_MS)).current;

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      debouncedOnChange.cancel();
    },
    [debouncedOnChange],
  );

  const handleClose = () => {
    debouncedOnChange.cancel();
    setValue("");
    onChange("");
    setIsOpen(false);
  };

  const handleChange = (nextValue: string) => {
    setValue(nextValue);
    debouncedOnChange(nextValue);
  };

  const searchButton = (
    <div className={isExpandedOnMobile ? "hidden md:flex" : undefined}>
      <IconButton
        onClick={() => setIsOpen(true)}
        aria-label={placeholder}
        icon="magnifying-glass"
        size="sm"
        variant="outline"
      />
    </div>
  );

  if (!isOpen && !isExpandedOnMobile) {
    return searchButton;
  }

  if (!isOpen) {
    return (
      <>
        {searchButton}
        {isExpandedOnMobile && (
          <SearchInputField
            className={MOBILE_INPUT_CLASS_NAME}
            inputClassName={inputClassName}
            inputRef={inputRef}
            onChange={handleChange}
            onClose={handleClose}
            placeholder={placeholder}
            shouldAnimate={false}
            shouldShowClose={false}
            value={value}
          />
        )}
      </>
    );
  }

  return (
    <SearchInputField
      inputClassName={inputClassName}
      inputRef={inputRef}
      onChange={handleChange}
      onClose={handleClose}
      placeholder={placeholder}
      value={value}
    />
  );
};
